import { supabase } from '../lib/supabase';

export const authRecoveryService = {
  /**
   * Get system health dashboard metrics
   */
  async getSystemHealthDashboard() {
    try {
      const { data, error } = await supabase?.rpc('get_system_health_dashboard');

      if (error) throw error;

      return {
        brokenAuthStates: data?.broken_auth_states || 0,
        captchaFailures24h: data?.captcha_failures_24h || 0,
        configIssues: data?.config_issues || 0,
        failedUserCreations: data?.failed_user_creations || 0,
        overallHealth: data?.overall_health || 'unknown',
        lastUpdated: data?.last_updated
      };
    } catch (error) {
      console.error('Error fetching system health dashboard:', error);
      throw error;
    }
  },

  /**
   * Get broken authentication states
   */
  async getBrokenAuthStates() {
    try {
      const { data, error } = await supabase?.rpc('get_broken_auth_states');

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        email: row?.email,
        fullName: row?.full_name,
        errorType: row?.error_type,
        errorDetails: row?.error_details,
        detectedAt: row?.detected_at,
        repairedAt: row?.repaired_at,
        repairedBy: row?.repaired_by
      })) || [];
    } catch (error) {
      console.error('Error fetching broken auth states:', error);
      throw error;
    }
  },

  /**
   * Clear broken authentication state
   */
  async clearBrokenAuthState(userId, repairMethod, notes) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase?.rpc('clear_broken_auth_state', {
        p_user_id: userId,
        p_admin_email: user?.email,
        p_repair_method: repairMethod,
        p_notes: notes
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to clear auth state');
      }

      return {
        success: true,
        message: data?.message,
        email: data?.email,
        repairMethod: data?.repair_method
      };
    } catch (error) {
      console.error('Error clearing broken auth state:', error);
      throw error;
    }
  },

  /**
   * Get CAPTCHA diagnostics
   */
  async getCaptchaDiagnostics() {
    try {
      const { data, error } = await supabase
        ?.from('captcha_diagnostics')
        ?.select('*')
        ?.order('last_checked_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        captchaProvider: row?.captcha_provider,
        isEnabled: row?.is_enabled,
        configuration: row?.configuration,
        failureCount: row?.failure_count,
        lastFailureAt: row?.last_failure_at,
        lastSuccessAt: row?.last_success_at,
        healthStatus: row?.health_status,
        lastCheckedAt: row?.last_checked_at
      })) || [];
    } catch (error) {
      console.error('Error fetching CAPTCHA diagnostics:', error);
      throw error;
    }
  },

  /**
   * Reset CAPTCHA configuration
   */
  async resetCaptchaConfiguration(provider = 'supabase_builtin') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase?.rpc('reset_captcha_configuration', {
        p_admin_email: user?.email,
        p_provider: provider
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to reset CAPTCHA configuration');
      }

      return {
        success: true,
        message: data?.message,
        provider: data?.provider
      };
    } catch (error) {
      console.error('Error resetting CAPTCHA configuration:', error);
      throw error;
    }
  },

  /**
   * Validate Supabase configuration
   */
  async validateSupabaseConfiguration() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase?.rpc('validate_supabase_configuration', {
        p_admin_email: user?.email
      });

      if (error) throw error;

      return {
        connection: data?.connection,
        databaseSchema: data?.database_schema,
        rlsPolicies: data?.rls_policies,
        overallStatus: data?.overall_status,
        validatedAt: data?.validated_at
      };
    } catch (error) {
      console.error('Error validating Supabase configuration:', error);
      throw error;
    }
  },

  /**
   * Get Supabase configuration validation history
   */
  async getConfigurationValidationHistory(limit = 20) {
    try {
      const { data, error } = await supabase
        ?.from('supabase_config_validation')
        ?.select('*')
        ?.order('validated_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        validationType: row?.validation_type,
        validationStatus: row?.validation_status,
        validationDetails: row?.validation_details,
        errorMessage: row?.error_message,
        suggestions: row?.suggestions,
        validatedAt: row?.validated_at,
        validatedBy: row?.validated_by
      })) || [];
    } catch (error) {
      console.error('Error fetching configuration validation history:', error);
      throw error;
    }
  },

  /**
   * Get failed user creations
   */
  async getFailedUserCreations() {
    try {
      const { data, error } = await supabase?.rpc('get_failed_user_creations');

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        email: row?.email,
        fullName: row?.full_name,
        companyName: row?.company_name,
        failureReason: row?.failure_reason,
        retryCount: row?.retry_count,
        recoveryStatus: row?.recovery_status,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching failed user creations:', error);
      throw error;
    }
  },

  /**
   * Retry failed user creation
   */
  async retryFailedUserCreation(failedCreationId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase?.rpc('retry_failed_user_creation', {
        p_failed_creation_id: failedCreationId,
        p_admin_email: user?.email
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to retry user creation');
      }

      return {
        success: true,
        message: data?.message,
        email: data?.email,
        retryInstructions: data?.retry_instructions
      };
    } catch (error) {
      console.error('Error retrying failed user creation:', error);
      throw error;
    }
  },

  /**
   * Get system repair audit log
   */
  async getSystemRepairAuditLog(filters = {}) {
    try {
      let query = supabase
        ?.from('system_repair_audit')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.repairType) {
        query = query?.eq('repair_type', filters?.repairType);
      }

      if (filters?.performedBy) {
        query = query?.ilike('performed_by', `%${filters?.performedBy}%`);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      const limit = filters?.limit || 50;
      query = query?.limit(limit);

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        repairType: row?.repair_type,
        targetEntityType: row?.target_entity_type,
        targetEntityId: row?.target_entity_id,
        actionTaken: row?.action_taken,
        actionDetails: row?.action_details,
        performedBy: row?.performed_by,
        success: row?.success,
        errorMessage: row?.error_message,
        executionTimeMs: row?.execution_time_ms,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching system repair audit log:', error);
      throw error;
    }
  },

  /**
   * Bulk clear broken auth states
   */
  async bulkClearAuthStates(userIds, repairMethod, notes) {
    try {
      const results = [];

      for (const userId of userIds) {
        try {
          const result = await this.clearBrokenAuthState(userId, repairMethod, notes);
          results?.push({ userId, success: true, ...result });
        } catch (error) {
          results?.push({ userId, success: false, error: error?.message });
        }
      }

      return results;
    } catch (error) {
      console.error('Error bulk clearing auth states:', error);
      throw error;
    }
  },

  /**
   * Bulk retry failed user creations
   */
  async bulkRetryUserCreations(failedCreationIds) {
    try {
      const results = [];

      for (const id of failedCreationIds) {
        try {
          const result = await this.retryFailedUserCreation(id);
          results?.push({ id, success: true, ...result });
        } catch (error) {
          results?.push({ id, success: false, error: error?.message });
        }
      }

      return results;
    } catch (error) {
      console.error('Error bulk retrying user creations:', error);
      throw error;
    }
  }
};

export default authRecoveryService;